// SPDX-License-Identifier: Elastic-2.0
/**
 * LockControls — two large buttons (lock / unlock) with confirmation
 * required for unlock.
 *
 * Per task spec § 5:
 *   - Two big buttons with current state highlighted.
 *   - "Unlock" requires a confirm step. v0.1 ships with the browser
 *     `confirm()` dialog (acceptable per task spec); v0.2 should
 *     replace it with a Modal-in-Modal for nicer UX. TODO comment
 *     below marks the upgrade path.
 *   - Actions: `lock` / `unlock`. Optimistic state patch:
 *     `{ locked: <bool> }`.
 *
 * Test-friendliness: the confirmation step calls `window.confirm()` which
 * tests can stub via `vi.spyOn(window, "confirm").mockReturnValue(true)`.
 */
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon/Icon";

import type { Device } from "@/api/gateway";

import styles from "../DeviceModal.module.css";

export interface LockControlsProps {
  device: Device;
  onAction: (
    action: string,
    params: Record<string, unknown>,
    optimisticStatePatch?: Record<string, unknown>,
  ) => void;
  disabled?: boolean;
}

function isLocked(device: Device): boolean {
  const state = device.state ?? {};
  return Boolean(state.locked ?? state.is_locked);
}

export function LockControls({
  device,
  onAction,
  disabled,
}: LockControlsProps): JSX.Element {
  const { t } = useTranslation();
  const locked = isLocked(device);

  function handleLock(): void {
    onAction("lock", {}, { locked: true });
  }

  function handleUnlock(): void {
    // v0.1: browser confirm. TODO(v0.2): replace with a confirmation Modal
    // rendered alongside the parent Modal — see task spec § 5.
    const confirmed = window.confirm(t("device_modal.controls.confirm_unlock"));
    if (!confirmed) return;
    onAction("unlock", {}, { locked: false });
  }

  return (
    <div className={styles.lockRow} data-testid="lock-controls">
      <Button
        type="button"
        variant={locked ? "primary" : "secondary"}
        size="lg"
        onClick={handleLock}
        disabled={disabled}
        aria-pressed={locked}
        data-testid="lock-button-lock"
      >
        <Icon name="lock" size={18} />
        {" "}
        {t("device_modal.controls.lock")}
      </Button>
      <Button
        type="button"
        variant={!locked ? "primary" : "secondary"}
        size="lg"
        onClick={handleUnlock}
        disabled={disabled}
        aria-pressed={!locked}
        data-testid="lock-button-unlock"
      >
        <Icon name="key" size={18} />
        {" "}
        {t("device_modal.controls.unlock")}
      </Button>
    </div>
  );
}

export default LockControls;
